import type { IBiometricAuthPort } from '@app/domain/ports';
import { WebAuthnError } from './errors';
function toBase64(u8: Uint8Array) {
  return btoa(String.fromCharCode(...u8));
}

function fromBase64(b64: string) {
  const bin = atob(b64);
  const arr = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
  return arr;
}

/**
 * WebAuthn adapter implementation for biometric authentication
 * Pure TypeScript implementation - framework-agnostic
 */
export class WebAuthnAdapter implements IBiometricAuthPort {
  async enrollBiometric(
    id: string,
    name: string,
    displayName: string,
    salt: string,
  ): Promise<{ credentialId: string; prfOutput: Uint8Array }> {
    const prfSalt = new TextEncoder().encode(salt);

    const publicKeyCredentialCreation: PublicKeyCredentialCreationOptions = {
      challenge: crypto.getRandomValues(new Uint8Array(32)),
      rp: { name: 'Personal Finance App' },
      user: { id: new TextEncoder().encode(id), name, displayName },
      // Public key credential parameters (specifies algorithm types)
      pubKeyCredParams: [
        { type: 'public-key', alg: -7 }, // ES256 (recommended)
        { type: 'public-key', alg: -257 }, // RS256
      ],
      authenticatorSelection: {
        // Enforce user verification (PIN or Biometric)
        userVerification: 'required',
        // Built-in biometric keys (e.g., Windows Hello)
        authenticatorAttachment: 'platform',
        residentKey: 'preferred',
        requireResidentKey: false,
      },
      timeout: 60000,
      extensions: {
        credProps: true,
        prf: {
          eval: {
            first: prfSalt,
          },
        },
      },
    };

    const cred = (await navigator.credentials.create({
      publicKey: publicKeyCredentialCreation,
    })) as PublicKeyCredential | null;
    if (!cred) throw new WebAuthnError('Credential creation failed');

    const rawId = new Uint8Array(cred.rawId);

    // Extract PRF output from creation response
    const prfResults = (
      cred as PublicKeyCredential & { getClientExtensionResults: () => { prf?: { results?: { first?: ArrayBuffer } } } }
    ).getClientExtensionResults()?.prf;
    if (!prfResults?.results?.first) {
      throw new WebAuthnError('Failed PRF extraction during enrollment');
    }
    const prfOutput = new Uint8Array(prfResults.results.first as ArrayBuffer);

    return { credentialId: toBase64(rawId), prfOutput };
  }

  async deriveKeyFromBiometric(credentialId: string, salt: string): Promise<Uint8Array> {
    const rawId = fromBase64(credentialId);
    const encoder = new TextEncoder();
    const prfSalt = encoder.encode(salt);

    const publicKey: PublicKeyCredentialRequestOptions = {
      challenge: crypto.getRandomValues(new Uint8Array(32)),
      allowCredentials: [{ type: 'public-key', id: rawId }],
      userVerification: 'required',
      timeout: 60000,
      extensions: {
        prf: {
          eval: {
            first: prfSalt,
          },
        },
      },
    };

    const assertion = (await navigator.credentials.get({ publicKey })) as PublicKeyCredential | null;
    if (!assertion) throw new WebAuthnError('Authentication failed');

    const prfResults = (
      assertion as PublicKeyCredential & {
        getClientExtensionResults: () => { prf?: { results?: { first?: ArrayBuffer } } };
      }
    ).getClientExtensionResults()?.prf;
    if (!prfResults?.results?.first) {
      throw new WebAuthnError('Failed PRF extraction during authentication');
    }

    return new Uint8Array(prfResults.results.first as ArrayBuffer);
  }

  // Check if PRF extension is supported
  async supportsPRF(): Promise<boolean> {
    return !!(
      window as typeof window & {
        PublicKeyCredential?: { isUserVerifyingPlatformAuthenticatorAvailable?: () => Promise<boolean> };
      }
    ).PublicKeyCredential?.isUserVerifyingPlatformAuthenticatorAvailable;
  }

  // Get assertion for a given credential id
  async getAssertion(credentialId: string): Promise<boolean> {
    const id = fromBase64(credentialId);
    const publicKey: PublicKeyCredentialRequestOptions = {
      challenge: crypto.getRandomValues(new Uint8Array(32)),
      allowCredentials: [{ type: 'public-key', id, transports: ['hybrid'] }],
      userVerification: 'required',
      timeout: 60000,
    };

    const assertion = (await navigator.credentials.get({ publicKey })) as PublicKeyCredential | null;
    return !!assertion;
  }
}
